import React, { useState, useRef, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { API, GUEST_MSG_LIMIT, getAuth, apiHeaders, uploadHeaders, Logo } from './chat/chatUtils';
import ChatSidebar from './chat/ChatSidebar';
import ChatHeader from './chat/ChatHeader';
import ChatInput from './chat/ChatInput';
import MessageBubble from './chat/MessageBubble';
import WelcomeScreen from './chat/WelcomeScreen';
import TalkMode from './chat/TalkMode';
import { LimitModal, ShareModal } from './chat/Modals';

export default function ChatPage() {
  return <ChatApp />;
}

function ChatApp() {
  const navigate = useNavigate();
  const [sessions, setSessions] = useState([]);
  const [folders, setFolders] = useState([]);
  const [activeSession, setActiveSession] = useState(null);
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [streamingText, setStreamingText] = useState('');
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [pendingFile, setPendingFile] = useState(null);
  const [showLimitModal, setShowLimitModal] = useState(false);
  const [showShareModal, setShowShareModal] = useState(false);
  const [showTalkMode, setShowTalkMode] = useState(false);
  const [shareLink, setShareLink] = useState('');
  const [guestMsgCount, setGuestMsgCount] = useState(0);
  const messagesEndRef = useRef(null);
  const scrollContainerRef = useRef(null);
  const userScrolledRef = useRef(false);
  const inputRef = useRef(null);

  const { user, token } = getAuth();
  const isGuest = !token;

  // Smart auto-scroll: only scroll if user hasn't manually scrolled up
  const smartScroll = useCallback(() => {
    if (userScrolledRef.current) return;
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, []);

  // Detect if user scrolled away from bottom
  const handleScroll = useCallback(() => {
    const el = scrollContainerRef.current;
    if (!el) return;
    const distFromBottom = el.scrollHeight - el.scrollTop - el.clientHeight;
    userScrolledRef.current = distFromBottom > 80;
  }, []);

  useEffect(() => { smartScroll(); }, [messages, streamingText, smartScroll]);

  useEffect(() => {
    fetchSessions();
    fetchFolders();
    setSidebarOpen(window.innerWidth >= 768);
  }, []);

  const fetchSessions = async () => {
    try {
      const res = await fetch(`${API}/api/chat/sessions`, { headers: apiHeaders() });
      if (res.ok) setSessions(await res.json());
    } catch (e) { console.error(e); }
  };

  const fetchFolders = async () => {
    try {
      const res = await fetch(`${API}/api/chat/folders`, { headers: apiHeaders() });
      if (res.ok) setFolders(await res.json());
    } catch { /* ignore */ }
  };

  const loadMessages = async (sessionId) => {
    try {
      const res = await fetch(`${API}/api/chat/sessions/${sessionId}/messages`, { headers: apiHeaders() });
      if (res.ok) {
        const msgs = await res.json();
        setMessages(msgs);
        if (isGuest) setGuestMsgCount(msgs.filter(m => m.role === 'user').length);
        userScrolledRef.current = false;
      }
    } catch (e) { console.error(e); }
  };

  const selectSession = async (session) => {
    setActiveSession(session);
    setStreamingText('');
    await loadMessages(session.session_id);
    if (window.innerWidth < 768) setSidebarOpen(false);
    inputRef.current?.focus();
  };

  const createNewChat = async () => {
    try {
      const res = await fetch(`${API}/api/chat/sessions`, {
        method: 'POST', headers: apiHeaders(), body: JSON.stringify({ title: 'New Chat' }),
      });
      const session = await res.json();
      setSessions(prev => [session, ...prev]);
      setActiveSession(session);
      setMessages([]);
      setStreamingText('');
      setPendingFile(null);
      userScrolledRef.current = false;
      if (window.innerWidth < 768) setSidebarOpen(false);
      inputRef.current?.focus();
    } catch (e) { console.error(e); }
  };

  const deleteSession = async (e, sessionId) => {
    e.stopPropagation();
    try {
      await fetch(`${API}/api/chat/sessions/${sessionId}`, { method: 'DELETE', headers: apiHeaders() });
      setSessions(prev => prev.filter(s => s.session_id !== sessionId));
      if (activeSession?.session_id === sessionId) { setActiveSession(null); setMessages([]); }
    } catch (e) { console.error(e); }
  };

  const logout = () => {
    localStorage.removeItem('opensov-token');
    localStorage.removeItem('opensov-user');
    window.location.reload();
  };

  const handleFileSelect = async (file) => {
    if (file.size > 10 * 1024 * 1024) { alert('File too large. Max 10MB.'); return; }
    const formData = new FormData();
    formData.append('file', file);
    try {
      const res = await fetch(`${API}/api/chat/upload`, { method: 'POST', headers: uploadHeaders(), body: formData });
      if (!res.ok) { const err = await res.json(); alert(err.detail || 'Upload failed'); return; }
      setPendingFile(await res.json());
    } catch { alert('Upload failed.'); }
  };

  const sendMessage = async () => {
    if ((!input.trim() && !pendingFile) || isLoading) return;

    if (isGuest && guestMsgCount >= GUEST_MSG_LIMIT) {
      setShowLimitModal(true);
      return;
    }

    let sessionId = activeSession?.session_id;
    if (!sessionId) {
      try {
        const titleText = input.trim() ? input.trim().slice(0, 50) : (pendingFile?.file_name || 'New Chat');
        const res = await fetch(`${API}/api/chat/sessions`, {
          method: 'POST', headers: apiHeaders(), body: JSON.stringify({ title: titleText }),
        });
        const session = await res.json();
        setSessions(prev => [session, ...prev]);
        setActiveSession(session);
        sessionId = session.session_id;
      } catch (e) { console.error(e); return; }
    }

    const msgText = input.trim() || (pendingFile ? `Analyze this file: ${pendingFile.file_name}` : '');
    const userMsg = { id: Date.now().toString(), role: 'user', content: msgText, file_name: pendingFile?.file_name, file_type: pendingFile?.file_type };
    setMessages(prev => [...prev, userMsg]);
    setInput('');
    setIsLoading(true);
    setStreamingText('');
    userScrolledRef.current = false;
    if (isGuest) setGuestMsgCount(prev => prev + 1);

    const payload = {
      session_id: sessionId, message: msgText,
      file_base64: pendingFile?.file_base64 || null, file_type: pendingFile?.file_type || null, file_name: pendingFile?.file_name || null,
    };
    setPendingFile(null);

    try {
      // Use SSE streaming endpoint
      const res = await fetch(`${API}/api/chat/send/stream`, {
        method: 'POST',
        headers: apiHeaders(),
        body: JSON.stringify(payload),
      });

      if (res.status === 403) { setShowLimitModal(true); setIsLoading(false); return; }
      if (!res.ok) throw new Error('Failed');

      const reader = res.body.getReader();
      const decoder = new TextDecoder();
      let accumulated = '';
      let assistantId = null;

      while (true) {
        const { done, value } = await reader.read();
        if (done) break;

        const chunk = decoder.decode(value, { stream: true });
        const lines = chunk.split('\n');

        for (const line of lines) {
          if (!line.startsWith('data: ')) continue;
          const dataStr = line.slice(6).trim();
          if (!dataStr) continue;

          try {
            const data = JSON.parse(dataStr);
            if (data.type === 'start') {
              assistantId = data.id;
            } else if (data.type === 'token') {
              accumulated += data.content;
              setStreamingText(accumulated);
            } else if (data.type === 'done') {
              assistantId = data.id;
              setStreamingText('');
              setMessages(prev => [...prev, { id: data.id, role: 'assistant', content: data.content }]);
            }
          } catch { /* skip malformed */ }
        }
      }

      // Fallback if stream ended without 'done' event
      if (accumulated && !assistantId) {
        setStreamingText('');
        setMessages(prev => [...prev, { id: Date.now().toString(), role: 'assistant', content: accumulated }]);
      }

      fetchSessions();
    } catch (e) {
      console.error(e);
      setStreamingText('');
      setMessages(prev => [...prev, { id: Date.now().toString(), role: 'assistant', content: 'Sorry, something went wrong. Please try again.' }]);
    } finally {
      setIsLoading(false);
      setStreamingText('');
    }
  };

  const handleRegenerate = async (messageId) => {
    if (!activeSession || isLoading) return;
    setIsLoading(true);
    setStreamingText('');
    userScrolledRef.current = false;
    try {
      const res = await fetch(`${API}/api/chat/regenerate`, {
        method: 'POST', headers: apiHeaders(),
        body: JSON.stringify({ session_id: activeSession.session_id, message_id: messageId }),
      });
      if (!res.ok) throw new Error('Failed');
      const data = await res.json();
      setMessages(prev => {
        const filtered = prev.filter(m => m.id !== messageId);
        return [...filtered, { id: data.id, role: 'assistant', content: data.content }];
      });
    } catch (e) {
      console.error(e);
    } finally {
      setIsLoading(false);
    }
  };

  const handleShare = async () => {
    if (!activeSession) return;
    try {
      const res = await fetch(`${API}/api/chat/sessions/${activeSession.session_id}/share`, {
        method: 'POST', headers: apiHeaders(),
      });
      if (!res.ok) return;
      const data = await res.json();
      setShareLink(`${window.location.origin}/shared/${data.share_id}`);
      setShowShareModal(true);
    } catch (e) { console.error(e); }
  };

  const handleExport = async (format = 'pdf') => {
    if (!activeSession) return;
    try {
      const res = await fetch(`${API}/api/chat/sessions/${activeSession.session_id}/export?format=${format}`, { headers: apiHeaders() });
      if (!res.ok) return;
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `${activeSession.title || 'chat'}.${format}`;
      a.click();
      URL.revokeObjectURL(url);
    } catch (e) { console.error(e); }
  };

  const lastAssistantIdx = messages.length - 1;

  return (
    <div className="h-[100dvh] flex bg-app text-content overflow-hidden" data-testid="chat-page">
      {sidebarOpen && (
        <div className="fixed inset-0 bg-black/60 backdrop-blur-sm z-30 md:hidden" onClick={() => setSidebarOpen(false)} data-testid="sidebar-overlay" />
      )}

      <ChatSidebar
        sessions={sessions} activeSession={activeSession}
        sidebarOpen={sidebarOpen} setSidebarOpen={setSidebarOpen}
        folders={folders}
        onNewChat={createNewChat} onSelectSession={selectSession}
        onDeleteSession={deleteSession} onLogout={logout}
        isGuest={isGuest} user={user}
        onRefreshSessions={fetchSessions} onRefreshFolders={fetchFolders}
      />

      <main className="flex-1 flex flex-col min-w-0 bg-app">
        <ChatHeader
          activeSession={activeSession} messages={messages}
          sidebarOpen={sidebarOpen} setSidebarOpen={setSidebarOpen}
          isGuest={isGuest} guestMsgCount={guestMsgCount}
          onShare={handleShare} onExport={handleExport}
          onTalkMode={() => setShowTalkMode(true)}
        />

        <div
          ref={scrollContainerRef}
          onScroll={handleScroll}
          className="flex-1 overflow-y-auto overscroll-contain scrollbar-thin"
          data-testid="messages-container"
        >
          {messages.length === 0 && !streamingText ? (
            <WelcomeScreen onSuggestion={(text) => { setInput(text); inputRef.current?.focus(); }} isGuest={isGuest} />
          ) : (
            <div className="max-w-3xl mx-auto px-4 md:px-6 py-6 space-y-6">
              {messages.map((msg, idx) => (
                <MessageBubble key={msg.id} message={msg}
                  onRegenerate={msg.role === 'assistant' && idx === lastAssistantIdx ? handleRegenerate : null} />
              ))}
              {streamingText && <MessageBubble message={{ role: 'assistant', content: streamingText }} isStreaming />}
              {isLoading && !streamingText && (
                <div className="flex gap-3 items-center py-2">
                  <Logo size="md" className="flex-shrink-0" />
                  <div className="flex gap-1 items-center">
                    <span className="w-1.5 h-1.5 bg-zinc-500 rounded-full animate-bounce [animation-delay:0ms]" />
                    <span className="w-1.5 h-1.5 bg-zinc-500 rounded-full animate-bounce [animation-delay:150ms]" />
                    <span className="w-1.5 h-1.5 bg-zinc-500 rounded-full animate-bounce [animation-delay:300ms]" />
                  </div>
                </div>
              )}
              <div ref={messagesEndRef} />
            </div>
          )}
        </div>

        {isGuest && messages.length > 0 && (
          <div className="px-3 py-2 text-center text-[12px] border-t border-line bg-app-alt text-content-3 font-sora" data-testid="guest-banner">
            <button onClick={() => navigate('/auth')} className="text-emerald-500 hover:underline underline-offset-2" data-testid="guest-signup-link">Sign up</button>
            {' '}to save conversations
          </div>
        )}

        <ChatInput
          input={input} setInput={setInput}
          isLoading={isLoading} pendingFile={pendingFile}
          setPendingFile={setPendingFile}
          onSend={sendMessage} onFileSelect={handleFileSelect}
          inputRef={inputRef}
        />
      </main>

      {showLimitModal && <LimitModal onClose={() => setShowLimitModal(false)} navigate={navigate} />}
      {showShareModal && <ShareModal link={shareLink} onClose={() => setShowShareModal(false)} />}
      {showTalkMode && (
        <TalkMode
          sessionId={activeSession?.session_id}
          onClose={() => { setShowTalkMode(false); if (activeSession) loadMessages(activeSession.session_id); fetchSessions(); }}
          onNewSession={(session) => { setActiveSession(session); setSessions(prev => [session, ...prev]); }}
        />
      )}
    </div>
  );
}
